//#region Libraries
import { useMemo } from "react";
//#endregion
//#region Styles
import {
	Column,
} from "components/styles";
import {
	InlineNote,
	PositionBadge,
	PositionBadgeItem,
	ResultBlock,
	ResultMeta,
	ResultValue,
	SignatureSlotCard,
	StatusDot,
	SlotHeader,
	SlotMeta,
	SlotStatus,
	SlotSummary,
} from "../../styles";
//#endregion
//#region Components
import CustomSelect from "components/CustomSelect/CustomSelect";
import ErrorMessage from "components/ErrorMessage/ErrorMessage";
//#endregion
//#region Icons
import CheckIcon from "icons/CheckIcon";
import ErrorIcon from "icons/ErrorIcon";
//#endregion
//#region Utils
import {
	CERTIFICATION_SIGNATURE_SOURCE_STATUS,
	getAvailableCertificationSignatureTypeOptions,
	getInstitutionalSignatureValidityErrorText,
	getCertificationTeacherReference,
	hasDuplicateSignatureTypeForIndex,
	isCertificationTeacherReferenceResolved,
	isInstitutionalCertificationSignature,
	signatureRequiresSpeakerData,
} from "views/Eventos/utils/certification";
//#endregion

const CertificationSignatureSlot = ({
	slotIndex,
	signature,
	allSignatures,
	error,
	signatureTypeOptions,
	organizerCampusName,
	teacherReference,
	onChange,
}) => {
	const requiresSpeakerData = signatureRequiresSpeakerData(
		signature.signatureTypeCode
	);

	const typeOptions = useMemo(
		() =>
			getAvailableCertificationSignatureTypeOptions(
				signatureTypeOptions,
				allSignatures,
				slotIndex
			).map(({ id, code, name }) => ({
				value: code,
				text: name,
				description: `Tipo de firma ${name}`,
				id,
			})),
		[allSignatures, signatureTypeOptions, slotIndex]
	);

	const changeSignatureType = value => {
		if (
			hasDuplicateSignatureTypeForIndex(
				allSignatures,
				slotIndex,
				value
			)
		)
			return;
		const typeDetail =
			signatureTypeOptions.find(({ code }) => code === value) || null;
		onChange({
			signatureTypeId: typeDetail?.id || 0,
			signatureTypeCode: value,
			eventSignatureId: 0,
			sourceStatus: value ? "PENDING" : "PENDING",
		});
	};

	const normalizedTeacherReference =
		teacherReference || getCertificationTeacherReference(null);
	const teacherResolved = isCertificationTeacherReferenceResolved(
		normalizedTeacherReference
	);
	const hasDuplicateType = hasDuplicateSignatureTypeForIndex(
		allSignatures,
		slotIndex,
		signature.signatureTypeCode
	);
	const institutionalValidityErrorText =
		getInstitutionalSignatureValidityErrorText(
			signature,
			organizerCampusName
		);
	const isInstitutionalPendingRefresh =
		isInstitutionalCertificationSignature(signature.signatureTypeCode) &&
		[
			CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS,
			CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_CAMPUS_REFRESH,
			CERTIFICATION_SIGNATURE_SOURCE_STATUS.PENDING_EVENT_END_DATE,
		].includes(signature.sourceStatus);
	const hasResolutionError =
		Boolean(institutionalValidityErrorText) ||
		Boolean(error?.fullName) ||
		Boolean(error?.signatureTypeCode) ||
		hasDuplicateType;
	const isResolved =
		requiresSpeakerData
			? teacherResolved && Boolean(signature.eventSignatureId)
			: Boolean(signature.eventSignatureId) &&
			  !institutionalValidityErrorText;
	const statusVariant = hasResolutionError
		? "error"
		: isResolved
			? "success"
			: "neutral";
	const statusText = hasResolutionError
		? "Revisar"
		: isResolved
			? "Validada"
			: "Pendiente";
	const detailItems = requiresSpeakerData
		? [
				Boolean(normalizedTeacherReference?.fullName) && {
					value: normalizedTeacherReference.fullName,
				},
				Boolean(normalizedTeacherReference?.utpCode) && {
					meta: `Código: ${normalizedTeacherReference.utpCode}`,
				},
		  ].filter(Boolean)
		: [
				Boolean(signature.fullName) && {
					value: signature.fullName,
				},
		  ].filter(Boolean);
	const inlineNote = isInstitutionalPendingRefresh
		? "Se actualizará con campus y fecha."
		: "";
	const shouldShowError = requiresSpeakerData
		? Boolean(error?.fullName) ||
		  !teacherResolved ||
		  Boolean(institutionalValidityErrorText)
		: Boolean(institutionalValidityErrorText);
	const errorText = requiresSpeakerData
		? institutionalValidityErrorText ||
		  "Completa el docente / ponente en 'Organizado por'."
		: institutionalValidityErrorText || "Sin firma vigente para este campus.";
	const primaryValue = detailItems.find(item => item.value)?.value || "";
	const metaValue = detailItems.find(item => item.meta)?.meta || "";
	const positionItems = [
		{ key: "LEFT", label: "IZQ" },
		{ key: "CENTER", label: "CEN" },
		{ key: "RIGHT", label: "DER" },
	];

	return (
		<SignatureSlotCard $statusVariant={statusVariant}>
			<SlotHeader>
				<SlotSummary>
					<h4>Firma {slotIndex + 1}</h4>
				</SlotSummary>
				<SlotMeta>
					<SlotStatus $variant={statusVariant}>
						{statusVariant === "success" ? (
							<CheckIcon color="var(--green-3)" />
						) : statusVariant === "error" ? (
							<ErrorIcon color="var(--red-3)" />
						) : statusVariant === "neutral" ? (
							<StatusDot />
						) : null}
						<span>{statusText}</span>
					</SlotStatus>
					<PositionBadge>
						{positionItems.map(item => (
							<PositionBadgeItem
								key={item.key}
								$active={signature.positionCode === item.key}
							>
								{item.label}
							</PositionBadgeItem>
						))}
					</PositionBadge>
				</SlotMeta>
			</SlotHeader>
			<CustomSelect
				label="Tipo de firma"
				value={signature.signatureTypeCode}
				onChange={changeSignatureType}
				optionsProps={typeOptions}
				defaultOptionText="Selecciona una firma"
				minWidth="100%"
				error={Boolean(error?.signatureTypeCode) || hasDuplicateType}
				errorText={
					hasDuplicateType
						? "Ese tipo de firma ya fue seleccionado en otro slot"
						: "Selecciona un tipo de firma"
				}
				width={{
					container: "100%",
					containerSelect: "100%",
				}}
			/>
			{!signature.signatureTypeCode ? null : (
				<Column gap="10px">
					{Boolean(primaryValue) && (
						<ResultBlock>
							<ResultValue>{primaryValue}</ResultValue>
							{Boolean(metaValue) && <ResultMeta>{metaValue}</ResultMeta>}
						</ResultBlock>
					)}
					{Boolean(inlineNote) && <InlineNote>{inlineNote}</InlineNote>}
					<ErrorMessage error={shouldShowError} text={errorText} />
				</Column>
			)}
		</SignatureSlotCard>
	);
};

export default CertificationSignatureSlot;
